import type { WebPageLoadEvent, WebPageLoadType } from '@doss/common-all'
import { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useMatches } from '~/router/hooks'
import { setGuestFrameObservationSink, type GuestFrameObservation } from './guest-frame-monitor'
import { createPageLoadTracker } from './page-load-tracker'
import {
  enqueueWebTelemetryEvent,
  flushWebPerfEvents,
  prepareWebPerfBeacon,
  setPerfBeaconIdentity,
} from './perf-beacon'
import { getInFlightOperationCount, subscribeToInFlightOperations } from './timing-link'

const derivePageType = (matches: ReturnType<typeof useMatches>): string => {
  for (let index = matches.length - 1; index >= 0; index--) {
    const handle = matches[index].handle
    if (handle?.pageType) {
      return handle.pageType
    }
  }
  return 'other'
}

// Plain module-scope wrappers (not the component/hook bodies react-compiler's purity check
// analyzes) around the two nondeterministic reads the tracker needs. Both only ever run later,
// from timer callbacks and the in-flight-count listener, never synchronously during render.
const now = (): number => performance.now()
const emitPerfEvent = (event: Omit<WebPageLoadEvent, 'ts'>): void => {
  enqueueWebTelemetryEvent({ ...event, ts: Date.now() })
}

const emitGuestObservation = (observation: GuestFrameObservation): void => {
  const ts = Date.now()
  if (observation.type === 'lifecycle') {
    enqueueWebTelemetryEvent({
      type: 'guest_frame_lifecycle',
      operation: observation.operation,
      status: observation.status,
      ...(observation.errorCode === undefined ? {} : { error_code: observation.errorCode }),
      value_ms: observation.durationMs,
      frame_count: observation.frameCount,
      ...(observation.path === undefined ? {} : { path: observation.path }),
      ts,
    })
    return
  }
  if (observation.type === 'resource') {
    enqueueWebTelemetryEvent({
      type: 'guest_frame_resource',
      status: observation.status,
      frame_count: observation.frameCount,
      visible_frame_count: observation.visibleFrameCount,
      responding_frame_count: observation.respondingFrameCount,
      dom_nodes: observation.domNodes,
      long_task_count: observation.longTaskCount,
      long_task_duration_ms: observation.longTaskDurationMs,
      max_long_task_duration_ms: observation.maxLongTaskDurationMs,
      sample_window_ms: observation.sampleWindowMs,
      memory_source: observation.memorySource,
      ...(observation.memoryUsedBytes === undefined
        ? {}
        : { memory_used_bytes: observation.memoryUsedBytes }),
      ...(observation.memoryTotalBytes === undefined
        ? {}
        : { memory_total_bytes: observation.memoryTotalBytes }),
      transfer_bytes: observation.transferBytes,
      decoded_bytes: observation.decodedBytes,
      cache_hit_count: observation.cacheHitCount,
      ts,
    })
    return
  }
  if (observation.type === 'span') {
    enqueueWebTelemetryEvent({
      type: 'client_span',
      trace_id: observation.traceId,
      span_id: observation.spanId,
      ...(observation.parentSpanId === undefined
        ? {}
        : { parent_span_id: observation.parentSpanId }),
      name: observation.name,
      start_ms: observation.startMs,
      end_ms: observation.endMs,
      status: observation.status,
      ...(observation.errorCode === undefined ? {} : { error_code: observation.errorCode }),
      ...(observation.embedId === undefined ? {} : { embed_id: observation.embedId }),
      ...(observation.viewContentId === undefined
        ? {}
        : { view_content_id: observation.viewContentId }),
      ts,
    })
    if (observation.flush === true) {
      // Trace-terminal span: flush promptly so the whole frame trace reaches the
      // collector inside the tail sampler's decision window, not on the 15s timer.
      void flushWebPerfEvents()
    }
    return
  }
  enqueueWebTelemetryEvent({
    type: 'guest_query',
    status: observation.status,
    source: observation.source,
    ...(observation.errorCode === undefined ? {} : { error_code: observation.errorCode }),
    ...(observation.statementId === undefined ? {} : { statement_id: observation.statementId }),
    ...(observation.statementRequestIndex === undefined
      ? {}
      : { statement_request_index: observation.statementRequestIndex }),
    ...(observation.traceId === undefined ? {} : { trace_id: observation.traceId }),
    value_ms: observation.durationMs,
    ts,
  })
}

type TrackedRoute = {
  pathname: string
  loadType: WebPageLoadType
  startedAt: number
}

export const PerfTelemetryTracker = () => {
  const { pathname } = useLocation()
  const { user, isLoading } = useGlobalContext()
  const identity = user === undefined ? undefined : `${user.id}:${user.organizationId}`
  const matches = useMatches()
  const pageType = derivePageType(matches)
  const tracksPageLoad = matches.some((match) => match.handle?.tracksPageLoad === true)
  const routeRef = useRef<TrackedRoute>({ pathname, loadType: 'initial', startedAt: 0 })
  const identityRef = useRef<string | undefined>(undefined)
  const hasTrackedIdentityRef = useRef(false)

  const tracker = useMemo(
    () =>
      createPageLoadTracker({
        now,
        getInFlightOperationCount,
        subscribeToInFlightOperations,
        setTimeout: (fn, ms) => setTimeout(fn, ms),
        clearTimeout: (timer) => clearTimeout(timer),
        emit: emitPerfEvent,
      }),
    [],
  )

  // Route guest lifecycle, query, and resource observations through the authenticated beacon.
  useEffect(() => setGuestFrameObservationSink(emitGuestObservation), [])

  // Classify every pathname transition after the document's first route as a route change.
  useEffect(() => {
    if (routeRef.current.pathname !== pathname) {
      routeRef.current = { pathname, loadType: 'route_change', startedAt: now() }
    }
  }, [pathname])

  // Cancel active work before changing the identity used by the beacon queue.
  useEffect(() => {
    if (identityRef.current !== identity) {
      tracker.cancel()
      if (identity !== undefined && hasTrackedIdentityRef.current) {
        routeRef.current = {
          ...routeRef.current,
          loadType: 'route_change',
          startedAt: now(),
        }
      }
      identityRef.current = identity
      if (identity !== undefined) {
        hasTrackedIdentityRef.current = true
      }
    }
    setPerfBeaconIdentity(identity)
    return () => {
      tracker.cancel()
      setPerfBeaconIdentity(undefined)
    }
  }, [identity, tracker])

  // Subscribe only after React commits the document-lifetime tracker.
  useEffect(() => {
    tracker.start()
    return () => {
      tracker.dispose()
    }
  }, [tracker])

  // Report loads the user walks out on: pagehide (navigation away, reload, tab close) marks an
  // in-progress load `abandoned` with its elapsed time — previously these worst-outcome loads
  // emitted nothing. visibilitychange→hidden deliberately does NOT abandon: tab-switching while a
  // page loads is common and usually temporary, so that load stays active and is still measured
  // by its eventual complete/timeout/interrupted (the beacon already flushes queued events when
  // the tab hides). The explicit flush here matters because pagehide can fire while
  // visibilityState still reads 'visible', which bypasses the beacon's hidden-state triggers.
  useEffect(() => {
    const handlePageHide = (): void => {
      tracker.abandon()
      void flushWebPerfEvents()
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [tracker])

  // Measure only authenticated app routes; public and unresolved-auth routes stay silent.
  useEffect(() => {
    if (isLoading || identity === undefined || !tracksPageLoad) {
      tracker.cancel()
      return
    }

    const route = routeRef.current
    prepareWebPerfBeacon()
    tracker.onRouteChange(pageType, route.loadType, { startedAt: route.startedAt })
  }, [identity, isLoading, pageType, pathname, tracker, tracksPageLoad])

  return null
}
